import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
        setMobileCatOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isDarkBg = !scrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 h-[70px] flex items-center transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60 shadow-sm'
            : 'bg-black/20 backdrop-blur-sm'
        }`}
      >
        <div className="w-full max-w-[1440px] mx-auto px-4 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            className={`flex items-center gap-2 transition-colors ${
              isDarkBg ? 'text-background-50' : 'text-foreground-900'
            }`}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-store-2-line text-xl"></i>
            </div>
            <span className="text-lg font-semibold font-heading tracking-tight whitespace-nowrap">
              hoobuy.boutique
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isDarkBg ? 'text-background-50/90 hover:text-background-50' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Início
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isDarkBg ? 'text-background-50/90 hover:text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                Categorias
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 w-[240px] max-h-[420px] overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/artigo/hoobuyspreadsheet-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isDarkBg ? 'text-background-50/90 hover:text-background-50' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Guia de Compras
            </Link>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:inline-flex items-center px-3.5 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap cursor-pointer ${
                isDarkBg
                  ? 'border-background-50/40 text-background-50 hover:bg-background-50/10'
                  : 'border-foreground-300 text-foreground-800 hover:bg-foreground-50'
              }`}
            >
              hoobuy sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              buy it now
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </div>
            </a>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isDarkBg ? 'text-background-50 hover:bg-background-50/10' : 'text-foreground-900 hover:bg-background-100'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div
          ref={mobileRef}
          className="fixed inset-0 z-40 bg-background-50 pt-[70px] md:hidden overflow-y-auto"
        >
          <div className="px-4 py-6 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 font-medium hover:bg-background-100 transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-home-4-line text-lg text-foreground-600"></i>
              </div>
              <span>Início</span>
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 font-medium hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-grid-line text-lg text-foreground-600"></i>
              </div>
              <span className="flex-1 text-left">Categorias</span>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200 space-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => { setMobileOpen(false); setMobileCatOpen(false); }}
                    className="block px-4 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/artigo/hoobuyspreadsheet-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 font-medium hover:bg-background-100 transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-book-open-line text-lg text-foreground-600"></i>
              </div>
              <span>Guia de Compras</span>
            </Link>
          </div>

          <div className="px-4 py-6 border-t border-background-200 mt-4">
            <div className="flex flex-col gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-foreground-300 text-foreground-800 text-sm font-medium hover:bg-background-100 transition-all"
              >
                hoobuy sheet
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </div>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all"
              >
                buy it now
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}